import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import merge from "@tinkoff/utils/object/mergeDeep";
import DateRangePicker from "../DateRangePicker";
import FilterPill from "../FilterPill";
import OptionList, { Option } from "../OptionList";
import asArray from "../../../lib/as-array";
import { appendTimezone, millisecondsToSeconds, timestampRangeString } from "../../../lib/date";
import Divider from "../Divider";
const CUSTOM_OPTION_VALUE = "custom";
const CUSTOM_OPTION_LABEL = "Custom";
function makeOption(value, label) {
    return { value, label };
}
const customOption = makeOption(CUSTOM_OPTION_VALUE, CUSTOM_OPTION_LABEL);
const isCustomValue = (value) => value.value === CUSTOM_OPTION_VALUE;
const daysAgo = (days, relativeTo) => {
    return relativeTo - 60 * 60 * 24 * days;
};
// Round the current time to the previous hour
const now = new Date();
now.setMinutes(0);
now.setSeconds(0);
now.setMilliseconds(0);
const nowTimestamp = millisecondsToSeconds(now.getTime());
const minDate = daysAgo(13 * 30, nowTimestamp);
export default function DateFilter(props) {
    const { name, defaultValue, enableCompare = true, disabled, removable, open, onChange, onRemove } = props;
    const options = props.options.concat(customOption);
    let defaultSelectedOption = options[0];
    let defaultCustomDateRange = {
        startDate: daysAgo(4, nowTimestamp),
        endDate: daysAgo(1, nowTimestamp),
    };
    let defaultIsComparing = false;
    let defaultCustomCompareDateRange = defaultCustomDateRange;
    if (defaultValue) {
        const defaultOptionValue = defaultValue.value;
        if ("secondsAgo" in defaultOptionValue) {
            // See if the default value is one of the relative date ranges in the options list
            const defaultOption = options.find((option) => option.value === String(defaultOptionValue.secondsAgo));
            if (defaultOption) {
                defaultSelectedOption = defaultOption;
            }
            else {
                // It's a relative date range but it's not in the list, so we create a custom item
                defaultSelectedOption = makeOption(String(defaultOptionValue.secondsAgo), CUSTOM_OPTION_LABEL);
            }
            if (enableCompare && defaultOptionValue.compare) {
                defaultIsComparing = true;
            }
        }
        else {
            // The default value is a custom date range
            defaultSelectedOption = makeOption(CUSTOM_OPTION_VALUE, CUSTOM_OPTION_LABEL);
            defaultCustomDateRange = defaultOptionValue;
            if (enableCompare && defaultOptionValue.compareStartDate && defaultOptionValue.compareEndDate) {
                defaultIsComparing = true;
                defaultCustomCompareDateRange = {
                    startDate: defaultOptionValue.compareStartDate,
                    endDate: defaultOptionValue.compareEndDate,
                };
            }
        }
    }
    const selected = useSignal(defaultSelectedOption);
    const customDateRange = useSignal(defaultCustomDateRange);
    const isComparing = useSignal(defaultIsComparing);
    const customCompareDateRange = useSignal(defaultCustomCompareDateRange);
    const customDateRangeOption = useComputed(() => makeOption(customDateRange.value, appendTimezone(timestampRangeString(customDateRange.value.startDate, customDateRange.value.endDate))));
    // Used to pass the selected label to the FilterPill
    const pillNameOption = useSignal(isCustomValue(selected.value) ? customDateRangeOption.value : selected.value);
    // Build a new FilterOption whenever the selected value or compare mode changes and notify
    // upstream components by calling onChange() with the new option.
    useSignalEffect(() => {
        let option = (() => {
            if (isCustomValue(selected.value)) {
                // Return a copy of the custom date range option so that we don't modify the original
                return Object.assign({}, customDateRangeOption.value);
            }
            else {
                const newValue = { secondsAgo: Number(selected.value.value) };
                if (isComparing.value) {
                    newValue.compare = true;
                }
                return makeOption(newValue, selected.value.label);
            }
        })();
        if (isComparing.value) {
            option.label = `${option.label} vs Previous Period`;
            if (isCustomValue(selected.value)) {
                option = merge(option, {
                    value: {
                        compareStartDate: customCompareDateRange.value.startDate,
                        compareEndDate: customCompareDateRange.value.endDate,
                    },
                });
            }
        }
        else {
            delete option.value.compareStartDate;
            delete option.value.compareEndDate;
        }
        pillNameOption.value = option;
        if (onChange) {
            onChange(option);
        }
    });
    const renderCompareSection = () => (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(Option, { option: { value: "true", label: "Compare Previous Period" }, onChange: (_, selected) => (isComparing.value = selected), selected: isComparing.value, multiple: true }), isComparing.value ? (_jsxs(_Fragment, { children: [_jsx(Divider, {}), isCustomValue(selected.value) && (_jsx(DateRangePicker, { previousPeriod: true, startDate: customCompareDateRange.value.startDate, endDate: customCompareDateRange.value.endDate, minDate: minDate, maxDate: nowTimestamp, onChange: ([startDate, endDate]) => (customCompareDateRange.value = { startDate, endDate }) }))] })) : null] }));
    return (_jsxs(FilterPill, { attributes: props.attributes, name: name, options: [pillNameOption.value], open: open, disabled: disabled, removable: removable, onRemove: onRemove, children: [_jsx(OptionList, { options: options, multiple: false, selected: asArray(selected.value), onChange: (option) => (selected.value = option) }), isCustomValue(selected.value) && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(DateRangePicker, { startDate: customDateRange.value.startDate, endDate: customDateRange.value.endDate, minDate: minDate, maxDate: nowTimestamp, onChange: ([startDate, endDate]) => (customDateRange.value = { startDate, endDate }) })] })), enableCompare ? renderCompareSection() : null] }));
}
